import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { TAnalyticsType, TRESPONSE_GET_ANALYTICS_CONFIG } from '@monorepo/types';
import { ANALYTIC_TYPE_STORAGE_KEY_MAP } from '../types/analytics.constants';
import { AnalyticsFacade } from './analytics.facade';

const CACHE_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours same as oi-app

type TAnalyticsConfigCacheEntry = {
  timestamp: number;
  config: TRESPONSE_GET_ANALYTICS_CONFIG;
};

@Injectable()
export class AnalyticsConfigResolverService {
  #storage: Promise<Storage> | null = null;

  constructor(private storage: Storage, private analyticsFacade: AnalyticsFacade) {}

  async getConfig(analyticsType: TAnalyticsType): Promise<TRESPONSE_GET_ANALYTICS_CONFIG | null> {
    const cachedConfig = await this.#getCachedConfig(analyticsType);
    if (cachedConfig) return cachedConfig;

    const config = await this.analyticsFacade.fetchAnalyticsConfig(analyticsType);
    if (config) await this.cacheConfig(analyticsType, config);
    return config;
  }

  #getStorage(): Promise<Storage> {
    if (!this.#storage)
      this.#storage = this.storage.create().catch((e) => {
        this.#storage = null;
        throw e;
      });
    return this.#storage;
  }

  async #getCachedConfig(analyticsType: TAnalyticsType) {
    try {
      const key = ANALYTIC_TYPE_STORAGE_KEY_MAP[analyticsType];
      const store = await this.#getStorage();

      const entry: TAnalyticsConfigCacheEntry | null = await store.get(key);

      if (!entry) return null;

      const age = Date.now() - entry.timestamp;
      if (!Number.isFinite(age) || age < 0 || age > CACHE_TTL_MS) return null;

      return entry.config;
    } catch (e) {
      console.error('[AnalyticsConfigResolverService] Error reading cache', e);
      return null;
    }
  }

  async cacheConfig(analyticsType: TAnalyticsType, config: TRESPONSE_GET_ANALYTICS_CONFIG): Promise<void> {
    try {
      const store = await this.#getStorage();
      const entry: TAnalyticsConfigCacheEntry = { config, timestamp: Date.now() };

      await store.set(ANALYTIC_TYPE_STORAGE_KEY_MAP[analyticsType], entry);
    } catch (e) {
      console.error('[AnalyticsConfigCache] Error writing cache', e);
    }
  }
}
